import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DossierAgendaConcurencyAction,
  DossierAgendaOverlapFiltersInput,
} from '@sc/data-access-graphql';
import {
  AgendaHttpService,
  DossierHttpService,
} from '@sc/data-access-http-services';
import { Dossier, LoggerLevelType } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { SnackbarService } from '@sc/feature-snackbar';
import { dayjs } from '@sc/utils';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DossierManagerService {
  dossier: Dossier;
  constructor(
    private readonly authOktaService: AuthService,
    private readonly dossierHttpService: DossierHttpService,
    private readonly agendaHttpService: AgendaHttpService,
    private readonly snackbarService: SnackbarService,
    private readonly translator: TranslatorService
  ) {}

  public setDossier(dossier: Dossier) {
    this.dossier = dossier;
  }

  public clearDossier() {
    this.dossier = null;
  }

  sendOpenDossierNotif(dossierId: string) {
    if (!dossierId) {
      return of(false);
    }

    return this.dossierHttpService.sendDossierConcurencyAction({
      id: dossierId,
      concurencyAction: DossierAgendaConcurencyAction.Open,
    });
  }

  sendCloseDossierNotif(dossierId: string) {
    if (!dossierId) {
      return of(false);
    }

    return this.dossierHttpService.sendDossierConcurencyAction({
      id: dossierId,
      concurencyAction: DossierAgendaConcurencyAction.Close,
    });
  }

  public onCloseDossier(): Observable<boolean> {
    if (
      this.dossier?.currentlyInUse?.userId ===
      this.authOktaService.getAccountLogin()
    ) {
      return this.sendCloseDossierNotif(this.dossier.id).pipe(
        catchError((error: HttpErrorResponse) => {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningForm.closeDossierConcuError'
            ),
            3,
            LoggerLevelType.INFO
          );

          console.error(error);
          return of(false);
        })
      );
    } else {
      return of(false);
    }
  }

  public getOverlappingDossiers(
    fitterIds: string[],
    filters: DossierAgendaOverlapFiltersInput
  ) {
    return this.agendaHttpService
      .getDossiersOverlap({
        fitterIds,
        filters,
      })
      .pipe(take(1));
  }

  dossierOpennedBySomeoneElse(dossier: Dossier) {
    const dossierOpenTTL = dayjs.duration(4, 'hours');
    const isOpenLockExpired = dayjs()
      .add(dossierOpenTTL.asMinutes(), 'minutes')
      .isBefore(dayjs(dossier?.currentlyInUse?.timestamp));

    return (
      !!dossier?.currentlyInUse &&
      !isOpenLockExpired &&
      dossier.currentlyInUse.userId?.toLowerCase() !==
        this.authOktaService.getAccountLogin().toLowerCase()
    );
  }
}
